/**
 * Shared proof-view constants and theorem lookup guards.
 */

import {
  GENERATOR_CODES,
  GENERATOR_META,
  isNeutralizationTheoremName,
  lookupNeutralizationTheoremMeta,
  type GeneratorCode,
  type NeutralizationTheoremCode,
} from "@csk/core";
import {
  GRAY,
  THEOREM_BG,
  THEOREM_COLOR,
  type CanonicalTheoremName,
} from "../design-system/tokens.js";

export type CrossmapGeneratorCode = GeneratorCode;

export const GENERATOR_ORDER: readonly CrossmapGeneratorCode[] = GENERATOR_CODES;

export const GENERATOR_NAME: Readonly<Record<CrossmapGeneratorCode, string>> = Object.fromEntries(
  GENERATOR_META.map((generator) => [generator.code, generator.shortName]),
) as Record<CrossmapGeneratorCode, string>;

export interface TheoremLookup {
  readonly code: NeutralizationTheoremCode;
  readonly name: CanonicalTheoremName;
}

export function isTheoremName(value: unknown): value is CanonicalTheoremName {
  return isNeutralizationTheoremName(value) && Object.hasOwn(THEOREM_COLOR, value);
}

export function lookupTheorem(code: string): TheoremLookup | undefined {
  const theorem = lookupNeutralizationTheoremMeta(code);
  return theorem === undefined ? undefined : { code: theorem.code, name: theorem.name };
}

/** Normalize a free-text search query for case-insensitive substring matching. */
export function normalizeQuery(value: string): string {
  return value.trim().toLowerCase();
}

/** Display fields for one theorem chip in a proof-view table. */
export interface TheoremPresentation {
  /** Short canonical code (e.g. `S`, `P`) — or the raw input when unknown. */
  readonly label: string;
  /** Full theorem name — or the raw input when unknown. */
  readonly title: string;
  /** Foreground color token. */
  readonly color: string;
  /** Background color token. */
  readonly bg: string;
}

/**
 * Resolve a theorem code to its presentation fields, degrading gracefully to a
 * neutral gray chip when the code is not in the canonical registry.
 */
export function theoremPresentation(theoremCode: string): TheoremPresentation {
  const theorem = lookupTheorem(theoremCode);
  if (theorem === undefined) {
    return {
      label: theoremCode,
      title: theoremCode,
      color: GRAY[700],
      bg: GRAY[100],
    };
  }
  return {
    label: theorem.code,
    title: theorem.name,
    color: THEOREM_COLOR[theorem.name],
    bg: THEOREM_BG[theorem.name],
  };
}
